/**
 * Root component for the embedded cart.
 *
 * Composition and wiring only — the work happens in hooks:
 *
 *   useCartState      — cart contents, persisted in a cookie shared with checkoutdwa
 *   useCartPricing    — pricing for those contents
 *   useCartVisibility — open/close state, excluded paths, keyboard and focus behavior
 *
 * Those three used to live inline here, which meant this file knew about pricing
 * endpoint shapes and request bodies as well as rendering.
 */
import React, { useCallback } from 'react';
import CartPanel from './CartPanel.js';
import CartThemeProvider from './theme/CartThemeProvider.js';
import useCartState from './hooks/useCartState.js';
import useCartPricing from './hooks/useCartPricing.js';
import useCartVisibility from './hooks/useCartVisibility.js';

const Cart = ({
    config,
}) => {
    const {
        products,
        catalog,
        currency,
        isCartPanelOpen,
        removeProduct,
        totalQuantity,
        updateCartState,
        updateProductQuantity,
    } = useCartState();

    const { pricing, isLoading } = useCartPricing({
        sourceUrl: config.sourceUrl,
        lineItems: products,
        currency,
        totalQuantity,
    });

    const { closeCart } = useCartVisibility({
        excludedPaths: config.excludedPaths,
        isCartPanelOpen,
        updateCartState,
    });

    const handleCheckoutClicked = useCallback(() => {
        // Note: intentionally not firing the cart close event as we redirect, so that using
        // the back button will re-open the cart on the host page.
        window.location = config.checkoutUrl ?? `${config.sourceUrl}/order/checkout/shipping`;
    }, [config.checkoutUrl, config.sourceUrl]);

    return (
        <CartThemeProvider tokenOverrides={config.tokens}>
            <CartPanel
                isLoading={isLoading}
                isCartPanelOpen={isCartPanelOpen}
                config={config}
                catalog={catalog}
                pricing={pricing}
                lineItems={products}
                totalQuantity={totalQuantity}
                onQuantityChange={updateProductQuantity}
                onRemoveProduct={removeProduct}
                onClose={closeCart}
                onCheckoutClicked={handleCheckoutClicked}
            />
        </CartThemeProvider>
    );
};

export default Cart;
